/**
 * Mega Menu Tabbed — custom element for sidebar-tabbed mega menu navigation.
 *
 * Handles:
 * - Hover on sidebar tab → switches active panel
 * - Keyboard: Arrow keys between tabs, Enter/Space to activate, Tab into link grid
 * - ARIA: tablist/tab/tabpanel roles with aria-selected management
 * - Resets to first tab when menu opens (mouseenter on wrapper)
 */

class MegaMenuTabbed extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.activeIndex = 0;
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.megamenu-tabbed__tab'));
    this.panels = Array.from(this.querySelectorAll('.megamenu-tabbed__panel'));

    if (!this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('mouseenter', () => this.activateTab(i));
      tab.addEventListener('focus', () => this.activateTab(i));
      tab.addEventListener('keydown', (e) => this.handleKeydown(e, i));
    });

    // Reset to first tab when the menu becomes visible
    const wrapper = this.closest('.megamenu-tabbed__wrapper');
    if (wrapper) {
      wrapper.addEventListener('mouseenter', () => this.activateTab(0));
    }
  }

  activateTab(index) {
    if (index === this.activeIndex && this.tabs[index]?.classList.contains('is-active')) return;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.activeIndex = index;
  }

  handleKeydown(event, currentIndex) {
    let targetIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.tabs.length - 1;
        break;
      case 'Escape': {
        // Close the megamenu by removing hover state
        const navItem = this.closest('.header__nav__item');
        if (navItem) {
          const link = navItem.querySelector('.header__nav__link');
          if (link) link.focus();
        }
        return;
      }
      default:
        return;
    }

    if (targetIndex !== undefined) {
      this.tabs[targetIndex].focus();
    }
  }
}

customElements.define('mega-menu-tabbed', MegaMenuTabbed);
